<template>
  <TransitionRoot
    appear
    :show="isOpen"
    as="template"
  >
    <Dialog
      as="div"
      class="relative z-50"
      @close="close"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/30" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div
            class="pointer-events-none fixed inset-y-0 right-0 flex max-w-[500px]"
          >
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-300"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
            >
              <DialogPanel
                class="pb-18 pointer-events-auto w-screen max-w-md bg-white shadow-xl"
              >
                <img
                  v-if="isOfferLoading"
                  class="loading m-auto mt-16 h-12 w-12"
                  src="@/assets/loading-v2-yellow.svg"
                  alt="loading spinner"
                />
                <div
                  v-else
                  class="flex h-full max-h-screen flex-col overflow-y-auto py-6"
                >
                  <DialogTitle>
                    <div class="flex items-center justify-between gap-2 px-6">
                      <div class="mb-1 flex items-center gap-1">
                        <img
                          class="-ml-1 h-5 w-5 rotate-180 cursor-pointer md:hidden"
                          src="@/assets/icon-next.svg"
                          alt="close"
                          @click="close"
                        />
                        <router-link
                          :to="{
                            name: 'Load Detail',
                            params: { id: offer?.load?.id },
                          }"
                          class="text-xl font-medium text-steel-1100 hover:underline"
                        >
                          {{ offer?.load?.external_order_or_load_number }}
                        </router-link>
                      </div>
                      <img
                        class="cursor-pointer"
                        src="@/assets/close-icon-gray.svg"
                        alt="close"
                        @click="close"
                      />
                    </div>
                  </DialogTitle>

                  <div>
                    <div
                      class="flex flex-col items-start gap-2 border-b border-steel-300 bg-white px-6 pb-4 shadow-md"
                    >
                      <img
                        class="h-6 text-2xs"
                        :src="offer?.load?.broker_logo?.url"
                        :alt="offer?.load?.broker_name"
                      />

                      <a
                        :href="`mailto:${offer?.load?.broker_email}`"
                        class="text-sm font-normal text-steel-900 underline"
                      >
                        {{ offer?.load?.broker_email || '-' }}
                      </a>
                      <a
                        v-if="offer?.load?.broker_phone_number"
                        :href="`tel:+${offer?.load?.broker_phone_number}`"
                        class="-mt-1 mb-1 text-sm font-normal text-steel-900"
                      >
                        {{ formatPhone(offer?.load?.broker_phone_number) }}
                      </a>

                      <div class="flex w-full justify-between">
                        <div class="flex flex-col gap-1">
                          <div>
                            <p class="font-normal text-steel-1100">
                              {{ origin?.address }}
                            </p>
                            <p class="text-sm font-normal text-steel-800">
                              {{ origin?.time }}
                            </p>
                          </div>

                          <div>
                            <p class="font-normal text-steel-1100">
                              {{ destination?.address }}
                            </p>
                            <p class="text-sm font-normal text-steel-800">
                              {{ destination?.time }}
                            </p>
                          </div>
                        </div>

                        <div class="flex flex-col items-end gap-1">
                          <p class="text-1100 text-xl font-semibold">
                            {{ formatPrice(offer.load?.price) }}
                          </p>
                          <p
                            v-if="
                              offer.load?.rate_per_mile || offer.load?.price
                            "
                            class="text-sm font-normal text-steel-900"
                          >
                            {{
                              offer.load?.rate_per_mile
                                ? formatPrice(offer.load?.rate_per_mile)
                                : formatRatePerMile(
                                    offer.load?.price,
                                    offer.load?.posted_miles
                                  )
                            }}/mi
                          </p>
                          <p
                            v-else
                            class="h-4 text-sm font-normal text-steel-900"
                          ></p>
                          <span
                            v-if="
                              offer?.negotiation?.current_offer_rate
                                ?.direction === 'carrier' &&
                              offer?.negotiation?.current_offer_rate
                                ?.rate_type === 'offer' &&
                              offer?.status === 'pending'
                            "
                            class="rounded-full bg-steel-100 px-2 py-0.5 text-xs text-steel-800"
                          >
                            Offer Placed
                          </span>
                          <span
                            v-else-if="
                              offer?.negotiation?.current_offer_rate
                                ?.rate_type === 'counter' &&
                              offer?.status === 'pending'
                            "
                            class="rounded-full bg-ocean-100 px-2 py-0.5 text-xs text-ocean-800"
                          >
                            Offer Countered
                          </span>
                          <span
                            v-else-if="offer?.status === 'accepted'"
                            class="rounded-full bg-forest-200 px-2 py-0.5 text-xs text-forest-900"
                          >
                            Awarded
                          </span>
                          <span
                            v-else-if="
                              offer?.status === 'rejected' &&
                              offer?.rejected_by?.company_type_name ===
                                'carrier'
                            "
                            class="rounded-full bg-steel-100 px-2 py-0.5 text-xs text-steel-800"
                          >
                            Withdrawn
                          </span>
                          <span
                            v-else-if="offer?.status === 'rejected'"
                            class="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-600"
                          >
                            Rejected
                          </span>
                          <span
                            v-else-if="offer?.status === 'booked'"
                            class="rounded-full bg-ocean-100 px-2 py-0.5 text-xs text-ocean-800"
                          >
                            Booked
                          </span>
                          <span
                            v-else-if="offer?.status === 'expired'"
                            class="rounded-full bg-pumpkin-100 px-2 py-0.5 text-xs text-pumpkin-900"
                          >
                            Expired
                          </span>
                        </div>
                      </div>
                    </div>

                    <div
                      id="offer-actions-container"
                      class="flex max-h-[calc(100vh-264px)] flex-col gap-1 overflow-scroll px-6 py-3"
                      :class="{
                        'pb-20': counterInput,
                      }"
                    >
                      <div
                        v-for="(action, index) in offer_actions"
                        :key="action?.id"
                        class="flex w-full flex-col"
                      >
                        <div
                          v-if="action.showDate"
                          class="mb-4 flex items-center gap-2"
                        >
                          <span
                            class="mb-[1px] h-1 w-full border-b border-steel-400"
                          ></span>

                          <p class="min-w-max text-steel-800">
                            {{ action.displayDate }}
                          </p>

                          <span
                            class="mb-[1px] h-1 w-full border-b border-steel-400"
                          ></span>
                        </div>

                        <div
                          class="mb-2 flex w-max flex-col"
                          :class="{
                            'ml-auto items-end':
                              action?.direction === 'carrier',
                          }"
                        >
                          <div class="flex items-center gap-1">
                            <p
                              v-if="action.user?.first_name"
                              class="text-sm font-medium text-steel-900"
                            >
                              {{ action.user?.first_name }}
                              {{ action.user?.last_name }}
                            </p>
                            <p
                              v-else
                              class="text-sm font-medium text-steel-900"
                            >
                              {{ offer?.load?.broker_name }}
                            </p>
                            <p
                              class="mt-[2px] text-xs font-normal text-steel-900"
                            >
                              {{
                                dayjs(action?.created_at)?.format(' • h:mm a')
                              }}
                            </p>
                          </div>

                          <p
                            v-if="
                              action?.direction === 'carrier' &&
                              action?.rate_type === 'offer'
                            "
                            class="mt-[2px] pb-1 text-xs font-normal text-steel-900"
                          >
                            You made an offer:
                          </p>

                          <p
                            v-else-if="
                              action?.direction === 'broker' &&
                              action?.rate_type === 'counter'
                            "
                            class="mt-[2px] pb-1 text-xs font-normal text-steel-900"
                          >
                            You've received a counter offer:
                          </p>

                          <p
                            v-else-if="
                              action?.direction === 'carrier' &&
                              action?.rate_type === 'counter'
                            "
                            class="mt-[2px] pb-1 text-xs font-normal text-steel-900"
                          >
                            You countered an offer:
                          </p>

                          <div
                            class="flex w-max flex-col gap-1 rounded-lg p-2 shadow-md"
                            :class="{
                              'bg-forest-400': action?.type === 'accepted',
                              'bg-steel-100': action?.type !== 'accepted',
                            }"
                          >
                            <div class="flex items-center gap-1">
                              <p
                                class="text-xl font-medium"
                                :class="{
                                  'text-steel-200': action?.type === 'accepted',
                                  'text-steel-1100':
                                    action?.type !== 'accepted',
                                }"
                              >
                                {{ formatPrice(action?.rate) }}
                              </p>
                              <p
                                class="text-xs font-normal"
                                :class="{
                                  'text-steel-200': action?.type === 'accepted',
                                  'text-steel-700': action?.type !== 'accepted',
                                }"
                              >
                                •
                                {{
                                  action?.rate_per_mile
                                    ? formatPrice(action?.rate_per_mile)
                                    : formatRatePerMile(
                                        action?.rate,
                                        offer?.load?.posted_miles
                                      )
                                }}
                                /mi
                              </p>
                            </div>

                            <p
                              v-if="action?.direction === 'carrier'"
                              class="text-xs font-normal text-steel-900"
                            >
                              Asking against:
                              {{
                                formatPrice(
                                  index === 0
                                    ? offer?.load?.price
                                    : offer_actions[index - 1]?.rate
                                )
                              }}
                            </p>
                            <p
                              v-else-if="index !== 0"
                              class="text-xs font-normal text-steel-900"
                            >
                              vs your:
                              {{ formatPrice(offer_actions[index - 1]?.rate) }}
                            </p>
                          </div>
                        </div>
                      </div>

                      <p
                        v-if="
                          offer?.status === 'rejected' &&
                          offer?.rejected_by?.company_type_name === 'carrier'
                        "
                        class="ml-auto mt-3 text-xs text-steel-900"
                      >
                        This offer was withdrawn on
                        {{
                          dayjs(offer?.closed_at || offer?.updated_at)?.format(
                            'MM/DD/YYYY [at] h:mm a'
                          )
                        }}.
                      </p>
                      <p
                        v-else-if="offer?.status === 'rejected'"
                        class="ml-auto mt-3 text-xs text-steel-900"
                      >
                        This offer was rejected on
                        {{
                          dayjs(offer?.closed_at || offer?.updated_at)?.format(
                            'MM/DD/YYYY [at] h:mm a'
                          )
                        }}.
                      </p>
                      <p
                        v-else-if="offer?.status === 'accepted'"
                        class="ml-auto mt-2 text-xs text-steel-900"
                      >
                        This offer was accepted on
                        {{
                          dayjs(offer?.closed_at || offer?.updated_at)?.format(
                            'MM/DD/YYYY [at] h:mm a'
                          )
                        }}.
                      </p>
                      <p
                        v-else-if="offer?.status === 'booked'"
                        class="ml-auto mt-2 text-xs text-steel-900"
                      >
                        This offer was booked on
                        {{
                          dayjs(offer?.closed_at || offer?.updated_at)?.format(
                            'MM/DD/YYYY [at] h:mm a'
                          )
                        }}.
                      </p>
                      <p
                        v-else-if="offer?.status === 'expired'"
                        class="ml-auto mt-2 text-xs text-steel-900"
                      >
                        This offer was expired on
                        {{
                          dayjs(offer?.closed_at || offer?.updated_at)?.format(
                            'MM/DD/YYYY [at] h:mm a'
                          )
                        }}.
                      </p>
                    </div>
                  </div>

                  <div
                    v-if="selectedTab !== 'History'"
                    class="absolute bottom-0 left-0 z-20 flex w-full flex-col justify-end gap-3 border-t border-steel-300 bg-white p-4 md:flex-row"
                  >
                    <div
                      v-if="counterInput"
                      class="flex w-full flex-col gap-3"
                    >
                      <p class="-mb-2 pl-1 text-steel-900">
                        {{ initialCounter ? 'Edit Offer' : 'Submit Counter' }}
                      </p>
                      <InputComponent
                        input-label=""
                        wrapper-class="flex items-center text-sm gap-1.5 px-2 min-w-[350px] [&_input]:!font-normal [&_input::placeholder]:!font-normal rounded-md h-10"
                        :placeholder="
                          initialCounter ? 'Enter offer' : 'Enter counter offer'
                        "
                        :default-input-value="counter"
                        :value="counter"
                        @input-change="handleCounterInput"
                      ></InputComponent>

                      <div
                        class="flex justify-end gap-3 [&>*]:flex-1 md:[&>*]:flex-initial"
                      >
                        <ButtonComponent
                          class="border border-steel-300"
                          @click="handleNotNow"
                        >
                          Not Now
                        </ButtonComponent>
                        <ButtonComponent
                          variant="secondary"
                          class="!min-w-[150px]"
                          :is-loading="is_counter_offer_loading"
                          :disabled="
                            counter <=
                            offer?.negotiation?.current_offer_rate?.rate
                          "
                          @click="submitCounter"
                        >
                          {{ initialCounter ? 'Edit Offer' : 'Submit Counter' }}
                        </ButtonComponent>
                      </div>
                    </div>

                    <div
                      v-else-if="selectedTab === 'Action'"
                      class="flex gap-3"
                    >
                      <ButtonComponent
                        variant="transparent"
                        @click="$emit('handle-reject', offer)"
                      >
                        Reject
                      </ButtonComponent>
                      <div
                        class="flex gap-3 [&>*]:flex-1 md:[&>*]:flex-initial"
                      >
                        <ButtonComponent
                          v-if="canCounter"
                          class="border border-steel-300"
                          @click="$emit('handle-counter')"
                        >
                          Counter
                        </ButtonComponent>
                        <ButtonComponent
                          variant="secondary"
                          class="!min-w-[85px]"
                          :is-loading="!!acceptLoading"
                          @click="$emit('handle-accept', offer)"
                        >
                          Accept
                        </ButtonComponent>
                      </div>
                    </div>

                    <div
                      v-else-if="selectedTab === 'Waiting'"
                      class="flex gap-3"
                    >
                      <div
                        class="flex w-full gap-3 [&>*]:flex-1 md:[&>*]:flex-initial"
                      >
                        <ButtonComponent
                          variant="transparent"
                          @click="$emit('handle-withdraw', offer)"
                        >
                          Withdraw
                        </ButtonComponent>
                        <ButtonComponent
                          class="!min-w-[80px] border border-steel-300"
                          @click="$emit('handle-edit', offer)"
                        >
                          Edit
                        </ButtonComponent>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, computed, watchEffect, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ButtonComponent, InputComponent } from '@/components';
import { formatPrice } from '@/utils';
import { formatPhone } from '@/common';

dayjs.extend(utc);
dayjs.extend(timezone);

const props = defineProps({
  modelValue: Boolean,
  offer: Object,
  counterInput: Boolean,
  selectedTab: String,
  acceptLoading: String,
  initialCounter: [String, Number],
});

const emit = defineEmits([
  'update:modelValue',
  'close-drawer',
  'handle-counter',
  'handle-withdraw',
  'handle-edit',
  'handle-accept',
  'handle-reject',
  'handle-update-selected-offer',
]);

const store = useStore();
const toast = useToast();

const isOpen = ref(false);

const counter = ref('');

watchEffect(() => {
  isOpen.value = !!props.modelValue;

  if (props?.initialCounter) {
    counter.value = props.initialCounter;
  }

  nextTick(() => {
    const actionsContainer = document.querySelector('#offer-actions-container');
    if (actionsContainer) {
      actionsContainer.scrollTop = actionsContainer.scrollHeight;
    }
  });
});

const close = () => {
  isOpen.value = false;
  emit('update:modelValue', false);
};

const canCounter = computed(
  () =>
    props?.offer?.load?.book_it_now_type === 'offers_only' ||
    props?.offer?.load?.book_it_now_type === 'offers_and_book_it_now'
);

const isOfferLoading = computed(() => false);

const is_counter_offer_loading = computed(
  () =>
    store.state.loads?.is_counter_offers_loading ||
    store.state.loads?.is_book_now_loading
);

const handleCounterInput = (input) => {
  const formattedInput = input.replace(/[$,.-]/g, '');

  counter.value = formatPrice(formattedInput, 0);
};

const offer_actions = computed(() => {
  const offer_rates = props?.offer?.negotiation?.offer_rates;

  return offer_rates.map((record, index) => {
    const currentDay = dayjs(record.created_at).format('MMM DD');
    const previousDay =
      index > 0
        ? dayjs(offer_rates[index - 1].created_at).format('MMM DD')
        : null;

    return {
      ...record,
      showDate: currentDay !== previousDay,
      displayDate: currentDay,
    };
  });
});

const formatPickup = (pickup, pickup_timezone, has_time = true) => {
  let date = dayjs(pickup);
  if (pickup_timezone) {
    date = dayjs(pickup).tz(pickup_timezone);
  }
  if (has_time) {
    return date.format('MM/DD • h:mm A');
  }
  return `${date.format('MM/DD')} • TBD`;
};

const formatRatePerMile = (rate, miles) => {
  return formatPrice(rate / miles);
};

const origin = computed(() => {
  const stop = props.offer?.load?.stops?.[0];

  if (!stop) {
    return {
      address: '-',
      time: '-',
    };
  }

  return {
    address: `${stop?.city}${stop?.state ? ', ' + stop?.state : ''}`,
    time: formatPickup(
      stop?.scheduled_window_started_at_in_stop_timezone,
      stop?.timezone,
      stop?.has_time
    ),
  };
});

const destination = computed(() => {
  const stop = props.offer?.load?.stops?.[props.offer?.load?.stops?.length - 1];

  if (!stop) {
    return {
      address: '-',
      time: '-',
    };
  }

  return {
    address: `${stop?.city}${stop?.state ? ', ' + stop?.state : ''}`,
    time: formatPickup(
      stop?.scheduled_window_started_at_in_stop_timezone,
      stop?.timezone,
      stop?.has_time
    ),
  };
});

const handleNotNow = () => {
  emit('handle-counter');
  close();
};

const submitCounter = async () => {
  const formattedCounter = counter.value?.replace(/[$,.-]/g, '');

  const company_id = props?.offer?.load?.broker_company_id;

  if (props?.initialCounter) {
    const expiresAtTime = dayjs().add(24, 'hour').format('YYYY-MM-DD HH:mm:ss');

    const res = await store.dispatch('loads/editOffer', {
      id: props?.offer?.id,
      params: {
        rate: formattedCounter,
        load_id: props?.offer?.load_id,
        expires_at: expiresAtTime,
      },
    });

    if ([200, 201, 204].includes(res?.status) && res.data) {
      emit('handle-update-selected-offer', res?.data);

      await store.dispatch('loads/getOffersByCompanyId', {
        company_id,
        type: 'waiting',
      });

      await toast.open({
        message: 'Offer successfully updated',
        position: 'top-right',
        duration: 5000,
      });
    }
  } else {
    const res = await store.dispatch('loads/counterOffer', {
      id: props?.offer.id,
      rate: formattedCounter,
    });

    if ([200, 201, 204].includes(res?.status) && res.data) {
      // need to refetch both actions and waiting information

      const findBrokerActionsOffers = store.state.loads?.action_offers?.find(
        (el) => el.company_id === company_id
      );

      if (findBrokerActionsOffers?.offers?.length === 1) {
        await store.dispatch('loads/getOffersByType', {
          type: 'actions',
        });
      }

      await store.dispatch('loads/getOffersByCompanyId', {
        company_id,
        type: 'actions',
      });

      await toast.open({
        message: 'Counter Offer submitted',
        position: 'top-right',
        duration: 5000,
      });

      emit('close-drawer');

      await store.dispatch('loads/getOffersByType', {
        type: 'waiting',
      });
    } else {
      toast.open({
        message:
          'There was an error submitting your counter offer. Please try again.',
        position: 'top-right',
        duration: 5000,
      });
    }
  }
};
</script>
